import { Box, ButtonBase, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

interface SideNavItemProps {
	title: string;
	path: string;
	icon?: React.ReactNode;
	disabled?: boolean;
	/** Icon-only rail: label moves into a tooltip. */
	collapsed?: boolean;
	/** Called after navigating — lets the mobile drawer close itself. */
	onNavigate?: () => void;
}

export const isPathActive = (pathname: string, path: string): boolean =>
	pathname === path || pathname.startsWith(`${path}/`);

export const SideNavItem = ({ title, path, icon, disabled = false, collapsed = false, onNavigate }: SideNavItemProps) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const active = isPathActive(pathname, path);

	const button = (
		<ButtonBase
			onClick={() => {
				navigate(path);
				onNavigate?.();
			}}
			disabled={disabled}
			aria-current={active ? 'page' : undefined}
			sx={{
				alignItems: 'center',
				borderRadius: 1,
				display: 'flex',
				justifyContent: 'flex-start',
				/* Same horizontal padding in both modes so the icon never slides during the width animation. */
				px: 1.5,
				py: '6px',
				height: 40,
				textAlign: 'left',
				width: '100%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				color: 'neutral.400',
				...(active && {
					backgroundColor: 'rgba(255, 255, 255, 0.08)',
					/* A 3px bar reads as "current" without relying on colour alone (rail has no label). */
					boxShadow: 'inset 3px 0 0 currentColor',
					color: 'primary.main',
				}),
				'&:hover': {
					backgroundColor: 'rgba(255, 255, 255, 0.04)',
				},
				'&.Mui-focusVisible': {
					outline: '2px solid',
					outlineColor: 'primary.main',
					outlineOffset: -2,
				},
			}}
		>
			{icon && (
				<Box
					component="span"
					sx={{
						alignItems: 'center',
						color: active ? 'primary.main' : 'neutral.400',
						display: 'inline-flex',
						justifyContent: 'center',
						width: 24,
						flexShrink: 0,
					}}
				>
					{icon}
				</Box>
			)}
			{/* Kept mounted in rail mode and faded, so the DOM shape never changes mid-animation. */}
			<Box
				component="span"
				sx={{
					color: active ? 'common.white' : disabled ? 'neutral.500' : 'neutral.400',
					flexGrow: 1,
					ml: 1.5,
					fontFamily: (theme) => theme.typography.fontFamily,
					fontSize: 14,
					fontWeight: 600,
					lineHeight: '24px',
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					opacity: collapsed ? 0 : 1,
					transition: (theme) => theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
				}}
			>
				{title}
			</Box>
		</ButtonBase>
	);

	return (
		<li>
			{collapsed ? (
				<Tooltip title={title} placement="right" arrow enterDelay={300}>
					{button}
				</Tooltip>
			) : (
				button
			)}
		</li>
	);
};
